/**
 * v3.21-1620 · Global Client Workspace context.
 *
 * The Client Workspace is the STANDARD operating experience for a rep
 * working a client.  It must be able to open:
 *   • without a call (before, after, or independently of any call)
 *   • from Ghost Daily Organizer row click
 *   • from Clients list
 *   • from ActiveCallPill (Notes / Onboarding / Email / Call Assist)
 *   • from missed-call follow-up
 *   • deep-linked via the ?workspace=<clientId> query param
 *
 * The workspace state lives here (a single source of truth) so that no
 * upstream component remount, hangup transition, or navigation can
 * destroy the working session.  Only an explicit `closeClientWorkspace()`
 * call (i.e. the user pressing the Close button) will actually close it.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

const ClientWorkspaceCtx = createContext(null);

const LS_OPEN_KEY = 'clientWorkspace.open.v1';

export function ClientWorkspaceProvider({ children }) {
  // Persist open-state in localStorage so a hard refresh / PWA reload
  // brings the workspace back to the same client the rep was working.
  const [openState, setOpenState] = useState(() => {
    try {
      const raw = window.localStorage.getItem(LS_OPEN_KEY);
      if (!raw) return { open: false, clientId: null, callSid: null };
      const parsed = JSON.parse(raw);
      if (parsed && parsed.clientId) {
        return {
          open:     true,
          clientId: parsed.clientId,
          callSid:  parsed.callSid || null,
        };
      }
    } catch { /* ignore */ }
    return { open: false, clientId: null, callSid: null };
  });

  const persist = useCallback((next) => {
    try {
      if (next.open && next.clientId) {
        window.localStorage.setItem(
          LS_OPEN_KEY,
          JSON.stringify({ clientId: next.clientId, callSid: next.callSid || null }),
        );
      } else {
        window.localStorage.removeItem(LS_OPEN_KEY);
      }
    } catch { /* ignore */ }
  }, []);

  const openClientWorkspace = useCallback((clientId, opts = {}) => {
    if (!clientId) return;
    const next = { open: true, clientId, callSid: opts.callSid || null };
    setOpenState(next);
    persist(next);
  }, [persist]);

  // Update the call context (SID) without touching open-state.
  // Used when a call becomes active / wraps up / completes for the
  // client already loaded in the workspace.
  const attachCallToWorkspace = useCallback((clientId, callSid) => {
    setOpenState((prev) => {
      if (!prev.open || prev.clientId !== clientId) return prev;
      const next = { ...prev, callSid: callSid || null };
      persist(next);
      return next;
    });
  }, [persist]);

  const closeClientWorkspace = useCallback(() => {
    setOpenState({ open: false, clientId: null, callSid: null });
    persist({ open: false });
  }, [persist]);

  // Global custom-event bridge so any component (Ghost Daily Organizer,
  // client rows, deep-link handlers) can request the workspace without
  // needing to import the hook directly.
  //
  // v3.21-1795 · Also listens for `phone-click:open-workspace` — the
  // event PhoneClickable's disableModal branch dispatches when the
  // operator clicks a Client phone in DealDashboard / ClientDetail /
  // ClientQuickView / anywhere else the Client is being worked.  The
  // workspace opens (if not already open) for that Client and the
  // modal itself listens for the same event to focus/highlight the
  // integrated CALL panel — no popup, no immediate dial, no
  // navigation.  The canonical outbound-call hook only fires when
  // the operator explicitly clicks "Call Now" inside the CALL
  // panel after seeing the Call As line + originating number.
  useEffect(() => {
    const onOpen = (e) => {
      const { clientId, callSid } = e.detail || {};
      if (clientId) openClientWorkspace(clientId, { callSid });
    };
    const onClose = () => closeClientWorkspace();
    const onAttach = (e) => {
      const { clientId, callSid } = e.detail || {};
      if (clientId) attachCallToWorkspace(clientId, callSid);
    };
    const onPhoneClick = (e) => {
      const { clientId } = e.detail || {};
      if (clientId) openClientWorkspace(clientId, {});
      // The workspace modal itself listens for this same event to
      // focus the CALL panel — nothing else to do here.
    };
    window.addEventListener('client-workspace:open',    onOpen);
    window.addEventListener('client-workspace:close',   onClose);
    window.addEventListener('client-workspace:attach',  onAttach);
    window.addEventListener('phone-click:open-workspace', onPhoneClick);
    return () => {
      window.removeEventListener('client-workspace:open',    onOpen);
      window.removeEventListener('client-workspace:close',   onClose);
      window.removeEventListener('client-workspace:attach',  onAttach);
      window.removeEventListener('phone-click:open-workspace', onPhoneClick);
    };
  }, [openClientWorkspace, closeClientWorkspace, attachCallToWorkspace]);

  const value = useMemo(() => ({
    ...openState,
    openClientWorkspace,
    closeClientWorkspace,
    attachCallToWorkspace,
  }), [openState, openClientWorkspace, closeClientWorkspace, attachCallToWorkspace]);

  return (
    <ClientWorkspaceCtx.Provider value={value}>
      {children}
    </ClientWorkspaceCtx.Provider>
  );
}

export function useClientWorkspace() {
  const ctx = useContext(ClientWorkspaceCtx);
  if (!ctx) {
    // Fallback so components mounted outside the provider still work
    // (e.g. unauthenticated shell).  Emits global events; no-op if the
    // provider isn't mounted at the moment.
    return {
      open:      false,
      clientId:  null,
      callSid:   null,
      openClientWorkspace: (clientId, opts = {}) => {
        try {
          window.dispatchEvent(new CustomEvent('client-workspace:open', {
            detail: { clientId, callSid: opts.callSid || null },
          }));
        } catch { /* ignore */ }
      },
      closeClientWorkspace: () => {
        try { window.dispatchEvent(new CustomEvent('client-workspace:close')); }
        catch { /* ignore */ }
      },
      attachCallToWorkspace: (clientId, callSid) => {
        try {
          window.dispatchEvent(new CustomEvent('client-workspace:attach', {
            detail: { clientId, callSid },
          }));
        } catch { /* ignore */ }
      },
    };
  }
  return ctx;
}
